import { useEffect, useState } from 'react'
import { FieldType, FieldValue, option, FilterField, Filters, O, Option } from '../../../../../features/ui'
import { Roadmap } from '../../../state/types'
import { DataType } from '../../../../../tables'
import { useRoadmapHooks } from '../../../hooks/roadmapHooks'
import { useDriverLoaders } from '../../../loaders/driverLoaders'
import { useVehicleLoaders } from '../../../loaders/vehicleLoaders'
import { PreparationStatus } from '../../../../common'
import { Labels } from '../../../../../features/data-types'

type Props = {
    roadmaps: Roadmap[]
    onSearchStart: () => void
    onSearchDone: (dataTypes: DataType[]) => void
}

const FiltersKey = 'roadmap'

export const RoadmapFilters = (props: Props) => {
    const { roadmaps, onSearchStart, onSearchDone } = props

    const [searchText, setSearchText] = useState<string>()
    const [dateFrom, setDateFrom] = useState<Date>()
    const [dateTo, setDateTo] = useState<Date>()
    const [prepStatus, setPrepStatus] = useState<O<PreparationStatus>>()
    const [driverId, setDriverId] = useState<string>()
    const [vehicleId, setVehicleId] = useState<string>()
    const [driverOptions, setDriverOptions] = useState<Option[]>([])
    const [vehicleOptions, setVehicleOptions] = useState<Option[]>([])

    const { searchRoadmaps } = useRoadmapHooks()
    const driverLoaders = useDriverLoaders()
    const vehicleLoaders = useVehicleLoaders()

    const loadDriverOptions = async () => {
        const { loadAllDrivers } = driverLoaders()
        const drivers = await loadAllDrivers(true)
        return drivers.map(driver =>
            option(driver.id, driver.name)
        )
    }

    const loadVehicleOptions = async () => {
        const { loadAllVehicles } = vehicleLoaders()
        const vehicles = await loadAllVehicles(true)
        return vehicles.map(vehicle =>
            option(vehicle.id, `${vehicle.name} (${vehicle.plate})`)
        )
    }

    useEffect(() => {
        loadDriverOptions().then(setDriverOptions)
        loadVehicleOptions().then(setVehicleOptions)
    }, [])

    useEffect(() =>
        search(searchText, dateFrom, dateTo, prepStatus, driverId, vehicleId)
    , [roadmaps])

    const search = (
        searchTextParam?: string,
        dateFromParam?: Date,
        dateToParam?: Date,
        prepStatusParam?: PreparationStatus,
        driverIdParam?: string,
        vehicleIdParam?: string
    ) => {
        onSearchStart()
        searchRoadmaps(roadmaps, searchTextParam, dateFromParam, dateToParam, prepStatusParam, driverIdParam, vehicleIdParam).then(searchedList => {
            setTimeout(() => onSearchDone(searchedList as DataType[]), 100)
        })
    }

    const handleSearch = (value?: FieldValue) => {
        const newSearchText = value as O<string>
        setSearchText(newSearchText)
        search(newSearchText, dateFrom, dateTo, prepStatus, driverId, vehicleId)
    }

    const handleDateFromChange = (value?: FieldValue) => {
        const newDateFrom = value as O<Date>
        newDateFrom?.setHours(0, 0, 0, 0)
        setDateFrom(newDateFrom)
        search(searchText, newDateFrom, dateTo, prepStatus, driverId, vehicleId)
    }

    const handleDateToChange = (value?: FieldValue) => {
        const newDateTo = value as O<Date>
        newDateTo?.setHours(23, 59, 59, 999)
        setDateTo(newDateTo)
        search(searchText, dateFrom, newDateTo, prepStatus, driverId, vehicleId)
    }

    const handlePrepStatusChange = (value?: FieldValue) => {
        const newPrepStatus = value as O<PreparationStatus>
        setPrepStatus(newPrepStatus)
        search(searchText, dateFrom, dateTo, newPrepStatus, driverId, vehicleId)
    }

    const handleDriverChange = (value?: FieldValue) => {
        const newDriverId = value as O<string>
        setDriverId(newDriverId)
        search(searchText, dateFrom, dateTo, prepStatus, newDriverId, vehicleId)
    }

    const handleVehicleChange = (value?: FieldValue) => {
        const newVehicleId = value as O<string>
        setVehicleId(newVehicleId)
        search(searchText, dateFrom, dateTo, prepStatus, driverId, newVehicleId)
    }

    const searchField: FilterField = {
        name: 'search',
        type: FieldType.TEXT,
        placeholder: 'Buscar por código, conductor o vehículo',
        icon: 'search',
        onChange: handleSearch
    }

    const filterFields: FilterField[] = [
        {
            name: 'preparationStatus',
            type: FieldType.SELECT,
            options: Object.values(PreparationStatus).map(status => option(status, Labels.preparationStatusLabel(status))),
            label: 'Estado de preparación',
            space: 3,
            onChange: handlePrepStatusChange
        },
        {
            name: 'driver',
            type: FieldType.SELECT,
            options: driverOptions,
            label: 'Conductor',
            space: 3,
            onChange: handleDriverChange
        },
        {
            name: 'vehicle',
            type: FieldType.SELECT,
            options: vehicleOptions,
            label: 'Vehículo',
            space: 3,
            onChange: handleVehicleChange
        },
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Fecha: Desde',
            space: 3,
            onChange: handleDateFromChange
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Fecha: Hasta',
            space: 3,
            onChange: handleDateToChange
        }
    ]

    return (
        <Filters
            filtersKey={FiltersKey}
            searchField={searchField}
            fields={filterFields}
            firstRowLength={3}
        />
    )
}
